
import { useState } from 'react';

const faqs = [
  {
    question: 'How does the AI receptionist handle calls?',
    answer:
      'Our AI receptionist uses advanced natural language processing to understand caller intent, answer questions, schedule appointments, and route calls — all in a natural, human-like conversation. It integrates directly with your calendar and CRM so nothing falls through the cracks.',
  },
  {
    question: 'Can the AI receptionist speak multiple languages?',
    answer:
      "Yes! Our AI supports over 30 languages fluently, including Spanish, French, Mandarin, Arabic, and more. It automatically detects the caller's language and responds accordingly, helping you serve a diverse customer base without hiring multilingual staff.",
  },
  {
    question: "What happens if the AI can't answer a question?",
    answer:
      "If the AI encounters a question outside its training, it will politely let the caller know and offer to transfer them to a live team member or take a detailed message. You'll receive an instant notification so you can follow up quickly.",
  },
  {
    question: 'How long does setup take?',
    answer:
      'Most businesses are up and running within 24 hours. Our onboarding team helps you configure your greeting, business hours, appointment types, and integrations. No technical expertise is required on your end.',
  },
  {
    question: "Can I customize the AI's voice and personality?",
    answer:
      'Absolutely. On the Professional and Enterprise plans, you can choose from a variety of voice options and customize the tone, greeting style, and conversational personality to match your brand identity perfectly.',
  },
  {
    question: 'Does it integrate with my existing tools?',
    answer:
      'Yes — we integrate seamlessly with Google Calendar, Outlook, Salesforce, HubSpot, Zoho, and many more. Our API also allows custom integrations for enterprise clients with unique workflows.',
  },
  {
    question: 'Is there a contract or can I cancel anytime?',
    answer:
      'All plans are month-to-month with no long-term contracts. You can cancel anytime with 30 days written notice and retain access through the end of your billing period. We also offer a 7-day free trial on every plan.',
  },
  {
    question: 'How is my data and customer information protected?',
    answer:
      'We use enterprise‑grade encryption for all data in transit and at rest. Our platform is SOC 2 compliant and follows strict data privacy protocols. We never sell or share your customer data with third parties.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-xs font-bold tracking-wider mb-4">
            FAQ
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Everything you need to know about our AI receptionist. Can&apos;t find
            what you&apos;re looking for?{' '}
            <a
              href="#contact"
              className="text-purple-600 hover:underline cursor-pointer font-medium"
            >
              Reach out to us
            </a>
            .
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-2xl border transition-all duration-300 ${
                  isOpen
                    ? 'border-purple-200 bg-gradient-to-br from-purple-50/60 to-pink-50/40 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between px-7 py-6 text-left cursor-pointer"
                >
                  <span
                    className={`text-base font-semibold pr-4 transition-colors ${
                      isOpen ? 'text-purple-700' : 'text-gray-900'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                      isOpen ? 'bg-purple-600 rotate-45' : 'bg-gray-100'
                    }`}
                  >
                    <i
                      className={`ri-add-line text-lg ${
                        isOpen ? 'text-white' : 'text-gray-500'
                      }`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-7 pb-6 text-gray-600 leading-relaxed text-sm">
                    {faq.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
